import { useCallback, useEffect, useRef, useState } from 'react';
import { imageSeconds, isVideo } from './playlist';

// Once every ad has failed in a row, wait before trying again so a dead network doesn't spin.
const RETRY_MS = 15000;

// `turn` counts every advance, so a one-ad playlist still restarts its ad.
export const useAdPlayback = (ads) => {
    const count = ads.length;
    const [position, setPosition] = useState({ index: 0, turn: 0 });
    const [allFailing, setAllFailing] = useState(false);
    const failures = useRef(0);
    const current = ads[position.index];

    const advance = useCallback(() => {
        setPosition((p) => ({ index: (p.index + 1) % Math.max(count, 1), turn: p.turn + 1 }));
    }, [count]);

    // A refresh can shorten the list while the current position is past its end.
    useEffect(() => {
        if (count > 0 && position.index >= count) setPosition((p) => ({ index: 0, turn: p.turn + 1 }));
    }, [count, position.index]);

    const reportPlayed = useCallback(() => {
        failures.current = 0;
    }, []);

    const reportFailure = useCallback(() => {
        failures.current += 1;
        if (failures.current >= count) setAllFailing(true);
        else advance();
    }, [count, advance]);

    useEffect(() => {
        if (!current || allFailing || isVideo(current)) return undefined;
        const timer = setTimeout(advance, imageSeconds(current) * 1000);
        return () => clearTimeout(timer);
    }, [current, position.turn, allFailing, advance]);

    useEffect(() => {
        if (!allFailing) return undefined;
        const timer = setTimeout(() => {
            failures.current = 0;
            setAllFailing(false);
            advance();
        }, RETRY_MS);
        return () => clearTimeout(timer);
    }, [allFailing, advance]);

    return { index: position.index, turn: position.turn, allFailing, advance, reportPlayed, reportFailure };
};
